import { Form, Input, Button, message, Card, Typography, App } from "antd";
import { useState } from "react";
import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import API from "../../../services/api";
import "./ForgotPassword.css";

const { Title, Text } = Typography;

function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message: messageApi } = App.useApp();

  // Rate limiting state
  const [canResend, setCanResend] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);

  const handleSubmit = async () => {
    messageApi.destroy();

    // Check if user can send request (rate limiting)
    if (!canResend) {
      messageApi.error(`Please wait ${timeLeft} seconds before trying again`);
      return;
    }

    // Check internet connection first
    if (!navigator.onLine) {
      messageApi.error("You are offline. Check your internet connection.");
      return;
    }

    // Get form values
    const values = form.getFieldsValue();

    // Check if email is empty
    if (!values.email) {
      messageApi.error("Please enter your email");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email)) {
      messageApi.error("Enter a valid email");
      return;
    }

    // If validation passes, submit the form
    try {
      setLoading(true);

      await API.post("/users/forgot-password", { email: values.email });

      messageApi.success("Reset link sent to your email");

      // Start rate limiting timer (2 minutes = 120 seconds)
      startCooldown();

    } catch (err) {
      messageApi.destroy();

      // Handle offline error during API call
      if (!navigator.onLine) {
        messageApi.error("You are offline. Check your internet connection.");
      } else {
        messageApi.error(err.response?.data?.message || "Error sending reset link");
      }
    } finally {
      setLoading(false);
    }
  };

  // Start cooldown timer
  const startCooldown = () => {
    setCanResend(false);
    setTimeLeft(120); // 2 minutes in seconds

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Format time for display (mm:ss)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check internet status when component mounts
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <Card className="forgot-password-card" variant="borderless">
          <div className="forgot-password-header">
            <div className="brand-section">
              <div className="brand-logo">D</div>
              <Title level={3} className="brand-name">Draptor</Title>
            </div>
            <Title level={4} className="forgot-password-title">
              Forgot password?
            </Title>
            <Text className="forgot-password-subtitle">
              Enter your email to receive reset link
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            validateTrigger={[]}
            className="forgot-password-form"
            requiredMark={false}
          >
            <Form.Item
              label="Email"
              name="email"
              className="form-item"
            >
              <Input
                placeholder="Enter your email"
                className="input-field"
                disabled={!isOnline}
              />
            </Form.Item>

            <Form.Item className="form-item">
              <Button
                type="primary"
                htmlType="button"
                block
                className="forgot-password-btn"
                loading={loading}
                onClick={handleSubmit}
                disabled={!isOnline || !canResend}
              >
                {!canResend ? `Try again in ${formatTime(timeLeft)}` : "Send reset link"}
              </Button>
            </Form.Item>

            {!canResend && (
              <div className="rate-limit-warning">
                <Text className="rate-limit-text">
                  ⏱️ You can request another reset link in {formatTime(timeLeft)}
                </Text>
              </div>
            )}

            {!isOnline && (
              <div className="offline-warning">
                <Text className="offline-text">
                  You are currently offline. Please check your internet connection.
                </Text>
              </div>
            )}

            <div className="back-to-login">
              <Text className="back-text">
                Remember your password? <Link to="/login">Back to login</Link>
              </Text>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default ForgotPassword;